/**
 * Schedule presentation (prompt.md §17, §18, §54).
 *
 * WHY the overlap policy and the missed-run policy are shown on every row: they
 * are the two settings that decide whether a schedule quietly skips a run, and a
 * user who cannot see them cannot tell a working schedule from a starved one.
 * A schedule that paused on a limit is called out explicitly, because the run it
 * produced is still resumable.
 */

import type { Schedule } from "@dayvise/domain";

import { Badge, EmptyState } from "../primitives/Feedback.tsx";
import { Button } from "../primitives/Button.tsx";
import { Card } from "../primitives/Surfaces.tsx";
import { MUTED_TEXT, rowStyle, stackStyle } from "../styles.ts";
import { formatRelativeTime, formatScheduleExpression } from "../logic/format.ts";

export interface ScheduleListProps {
  readonly schedules: readonly Schedule[];
  readonly onToggle?: (schedule: Schedule, enabled: boolean) => void | Promise<void>;
  readonly onTrigger?: (schedule: Schedule) => void | Promise<void>;
  readonly onOpen?: (schedule: Schedule) => void;
  readonly onDelete?: (schedule: Schedule) => void | Promise<void>;
  readonly busy?: boolean;
}

export function ScheduleList({ schedules, onToggle, onTrigger, onOpen, onDelete, busy = false }: ScheduleListProps) {
  if (schedules.length === 0) {
    return (
      <EmptyState
        title="No schedules"
        description="Schedules run in the cloud even when every client is closed. Create one to have an agent work on its own."
      />
    );
  }
  return (
    <div style={stackStyle("var(--dayvise-space-sm)")}>
      {schedules.map((schedule) => (
        <ScheduleCard
          key={schedule.scheduleId}
          schedule={schedule}
          onToggle={onToggle}
          onTrigger={onTrigger}
          onOpen={onOpen}
          onDelete={onDelete}
          busy={busy}
        />
      ))}
    </div>
  );
}

export interface ScheduleCardProps {
  readonly schedule: Schedule;
  readonly onToggle?: (schedule: Schedule, enabled: boolean) => void | Promise<void>;
  readonly onTrigger?: (schedule: Schedule) => void | Promise<void>;
  readonly onOpen?: (schedule: Schedule) => void;
  readonly onDelete?: (schedule: Schedule) => void | Promise<void>;
  readonly busy?: boolean;
}

function ScheduleCard({ schedule, onToggle, onTrigger, onOpen, onDelete, busy }: ScheduleCardProps) {
  const failing = schedule.consecutiveFailures > 0;
  return (
    <Card
      title={schedule.title}
      subtitle={formatScheduleExpression({ cronExpression: schedule.cronExpression, runAt: schedule.runAt, timezone: schedule.timezone })}
      footer={
        <div style={rowStyle("var(--dayvise-space-xs)")}>
          {onTrigger ? (
            <Button size="sm" variant="secondary" disabled={busy} onClick={() => void onTrigger(schedule)}>
              Run now
            </Button>
          ) : null}
          {onToggle ? (
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => void onToggle(schedule, !schedule.enabled)}>
              {schedule.enabled ? "Disable" : "Enable"}
            </Button>
          ) : null}
          {onOpen ? (
            <Button size="sm" variant="ghost" onClick={() => onOpen(schedule)}>
              Open
            </Button>
          ) : null}
          {onDelete ? (
            <Button size="sm" variant="danger" disabled={busy} onClick={() => void onDelete(schedule)}>
              Delete
            </Button>
          ) : null}
        </div>
      }
    >
      <div style={stackStyle("var(--dayvise-space-xs)")}>
        <div style={rowStyle("var(--dayvise-space-xs)", "flex-start")}>
          <Badge tone={schedule.enabled ? "success" : "neutral"}>{schedule.enabled ? "Enabled" : "Disabled"}</Badge>
          <Badge tone="neutral">overlap: {schedule.overlapPolicy}</Badge>
          <Badge tone="neutral">missed runs: {schedule.missedRunPolicy}</Badge>
          {failing ? <Badge tone="danger">{schedule.consecutiveFailures} consecutive failures</Badge> : null}
        </div>

        <p style={{ margin: 0, fontSize: "var(--dayvise-font-size-sm)" }}>{schedule.prompt}</p>

        <div style={rowStyle("var(--dayvise-space-md)", "flex-start")}>
          <span style={{ ...MUTED_TEXT, fontSize: "var(--dayvise-font-size-xs)" }}>
            Next run: {schedule.nextRunAt ? formatRelativeTime(schedule.nextRunAt) : "not scheduled"}
          </span>
          <span style={{ ...MUTED_TEXT, fontSize: "var(--dayvise-font-size-xs)" }}>
            Last run: {schedule.lastRunAt ? formatRelativeTime(schedule.lastRunAt) : "never"}
          </span>
          {schedule.notifyOnCompletion ? (
            <span style={{ ...MUTED_TEXT, fontSize: "var(--dayvise-font-size-xs)" }}>Notifies on completion</span>
          ) : null}
        </div>
      </div>
    </Card>
  );
}

export interface ScheduleEditorProps {
  readonly value: {
    readonly title: string;
    readonly prompt: string;
    readonly cron: string;
    readonly timezone: string;
    readonly overlapPolicy: "queue" | "skip" | "parallel";
    readonly missedRunPolicy: "skip" | "run_once" | "run_all";
  };
  readonly onChange: (next: ScheduleEditorProps["value"]) => void;
  readonly allowedOverlapPolicies: readonly ("queue" | "skip" | "parallel")[];
  readonly disabled?: boolean;
}

export interface ScheduleValidation {
  readonly field: string;
  readonly message: string;
}

/**
 * Client-side validation mirroring the server's `validateSchedule`, so a user
 * learns about a bad cron expression before a round trip. The server remains
 * authoritative — this only avoids an obviously doomed request.
 */
export function validateScheduleDraft(value: ScheduleEditorProps["value"]): ScheduleValidation[] {
  const errors: ScheduleValidation[] = [];
  if (value.title.trim().length === 0) errors.push({ field: "title", message: "Give the schedule a name you will recognise" });
  if (value.prompt.trim().length === 0) errors.push({ field: "prompt", message: "A schedule needs a prompt to run" });
  if (value.cron.trim().length === 0) errors.push({ field: "cron", message: "Enter a cron expression, for example 0 8 * * *" });
  else if (!looksLikeCron(value.cron)) errors.push({ field: "cron", message: "A cron expression needs five fields: minute hour day-of-month month day-of-week" });
  if (value.timezone.trim().length === 0) errors.push({ field: "timezone", message: "A timezone is required so the schedule fires at the hour you expect" });
  return errors;
}

/** Structural check only; the platform validates the expression properly. */
export function looksLikeCron(expression: string): boolean {
  const fields = expression.trim().split(/\s+/);
  if (fields.length !== 5) return false;
  return fields.every((field) => /^[0-9*,\-/A-Za-z]+$/.test(field));
}
